import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Archive,
  CalendarClock,
  CheckCheck,
  Compass,
  CreditCard,
  FolderKanban,
  LayoutGrid,
  LogOut,
  Menu,
  MonitorSmartphone,
  PenLine,
  Settings,
  Shield,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@orisini/ui/context/AuthContext'
import { Button } from '@orisini/ui/components/button'
import { cn } from '@orisini/ui/lib/utils'
import { contentApi } from '../../features/content/lib/content'

interface NavItem {
  label: string
  icon: LucideIcon
  /** Tanpa `to` = belum dibangun; tampil redup dengan label "Soon". */
  to?: string
  end?: boolean
  adminOnly?: boolean
  /** Angka di sisi kanan (mis. jumlah yang menunggu Review). */
  badge?: 'review'
}

/**
 * Struktur menu mengikuti docs/rencana-perombakan.md: OPERATIONS untuk kerja
 * harian, SYSTEM untuk perangkat & akun. Menu yang fasenya belum sampai sengaja
 * tetap terlihat (tanpa tautan) supaya bentuk akhirnya terbaca sejak awal.
 */
const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Main menu',
    items: [
      { label: 'Dashboard', icon: LayoutGrid, to: '/app', end: true },
      { label: 'Projects', icon: FolderKanban, to: '/projects' },
      { label: 'Discover', icon: Compass, to: '/discover' },
      { label: 'Create', icon: PenLine, to: '/create' },
      { label: 'Review', icon: CheckCheck, to: '/review', badge: 'review' },
      { label: 'Content Bank', icon: Archive, to: '/bank' },
      { label: 'Publishing Queue', icon: CalendarClock, to: '/queue' },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Orisini Agent', icon: MonitorSmartphone, to: '/devices' },
      { label: 'Subscription', icon: CreditCard, to: '/billing' },
      { label: 'Settings', icon: Settings, to: '/settings' },
      { label: 'Admin', icon: Shield, to: '/admin', adminOnly: true },
    ],
  },
]

/** Kerangka halaman orisini.com untuk area yang butuh login. */
export default function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  // Pindah halaman dari laci mobile menutup lacinya.
  useEffect(() => setMenuOpen(false), [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop: sidebar tetap di kiri. */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-border lg:block">
        <Sidebar />
      </aside>

      {/* Mobile: bilah atas + laci. */}
      <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-background px-4 py-3 lg:hidden">
        <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Dialog.Trigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu />
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
            <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r border-border bg-background">
              <Dialog.Title className="sr-only">Menu</Dialog.Title>
              <Dialog.Description className="sr-only">Main navigation</Dialog.Description>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="absolute right-2 top-2" aria-label="Close menu">
                  <X />
                </Button>
              </Dialog.Close>
              <Sidebar />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <span className="text-lg font-semibold tracking-tight">Orisini</span>
      </header>

      <main className="lg:pl-60">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function Sidebar() {
  const { me, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = me?.user.role === 'admin'
  const [review, setReview] = useState(0)

  // Jumlah yang menunggu keputusan; disegarkan setiap pindah halaman.
  useEffect(() => {
    contentApi
      .reviewCount()
      .then((r) => setReview(r.drafts + r.publish))
      .catch(() => undefined)
  }, [location.pathname])

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-4 text-lg font-semibold tracking-tight">Orisini</div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-4">
        {SECTIONS.map((section) => (
          <div key={section.title} className="space-y-1">
            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {section.title}
            </p>
            {section.items
              .filter((item) => !item.adminOnly || isAdmin)
              .map((item) => (
                <NavRow key={item.label} item={item} count={item.badge === 'review' ? review : 0} />
              ))}
          </div>
        ))}
      </nav>

      <div className="space-y-2 border-t border-border px-3 py-3">
        <p className="truncate px-2 text-sm text-muted-foreground" title={me?.user.email}>
          {me?.user.email}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={async () => {
            await logout()
            navigate('/login')
          }}
        >
          <LogOut /> Sign out
        </Button>
      </div>
    </div>
  )
}

function NavRow({ item, count }: { item: NavItem; count: number }) {
  const Icon = item.icon
  const base = 'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm [&_svg]:size-4 [&_svg]:shrink-0'

  if (!item.to) {
    return (
      <span className={cn(base, 'cursor-default text-muted-foreground/60')} aria-disabled="true">
        <Icon />
        {item.label}
        <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
          Soon
        </span>
      </span>
    )
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          base,
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )
      }
    >
      <Icon />
      {item.label}
      {count > 0 && <span className="ml-auto rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground tabular-nums">{count}</span>}
    </NavLink>
  )
}
